import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CoinsIcon, TrophyIcon, CheckIcon, AlertCircleIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import Button from '@/components/form/Button';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

interface PendingCashSnap {
  id: string;
  title: string;
  description: string;
  total_votes: number;
  ends_at: string;
  options: {
    id: string;
    title: string;
    description: string;
    vote_count: number;
  }[];
}

export default function CashSnapAdmin() {
  const navigate = useNavigate();
  const [pendingSnaps, setPendingSnaps] = useState<PendingCashSnap[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingSnap, setProcessingSnap] = useState<string | null>(null);
  const { user } = useAuth();
  
  useEffect(() => {
    // Check if user is admin
    if (user?.email !== 'dennishorn@gmx.net') {
      navigate('/dashboard');
      return;
    }
    
    fetchPendingCashSnaps();
  }, [user, navigate]);

  const fetchPendingCashSnaps = async () => {
    try {
      setLoading(true);
      
      // Get ended CASHSNAPs that haven't had prizes distributed yet
      const { data, error } = await supabase
        .from('snaps')
        .select(`
          id,
          title,
          description,
          total_votes,
          ends_at,
          options:snap_options!snap_options_snap_id_fkey (
            id,
            title,
            description,
            vote_count
          )
        `)
        .eq('cashsnap', true)
        .eq('prize_distributed', false)
        .is('winning_option_id', null)
        .lt('ends_at', new Date().toISOString());
      
      if (error) throw error;
      
      setPendingSnaps(data || []);
    } catch (error) {
      console.error('Error fetching pending CashSNAPs:', error);
      toast.error('Failed to load pending CashSNAPs');
    } finally {
      setLoading(false);
    }
  };

  const markWinnerAndDistributePrizes = async (snapId: string, optionId: string) => {
    try {
      setProcessingSnap(snapId);

      // Get the system token from environment variables
      const rawToken = import.meta.env.VITE_SYSTEM_TOKEN ?? '';
      const token = rawToken.trim();
      
      // Enhanced token validation
      if (!token || typeof token !== 'string' || !/^[0-9a-f]{64}$/.test(token)) {
        throw new Error('System token is not properly configured');
      }
      
      console.log(`Token length: ${token.length}`);

      // Create a combined function to handle both operations in one call
      const { error } = await supabase.rpc('admin_complete_cashsnap', {
        p_snap_id: snapId,
        p_option_id: optionId,
        p_amount_per_vote: 0.05,
        p_system_token: token
      });
      
      if (error) {
        if (error.message.includes('Invalid system token')) {
          throw new Error('System authentication failed - please check configuration');
        }
        throw new Error(`Operation failed: ${error.message}`);
      }
      
      toast.success('Winner marked and prizes distributed successfully!');
      
      // Refresh the list
      fetchPendingCashSnaps();
    } catch (error) {
      console.error('Error processing CashSnap:', error);
      
      // More specific error messages for different failure scenarios
      if (error instanceof Error) {
        if (error.message.includes('System token is not properly configured')) {
          toast.error('Missing or invalid system token configuration. Please check your environment variables.');
        } else if (error.message.includes('System authentication failed')) {
          toast.error('System token authentication failed. Please verify the token in your environment configuration.');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setProcessingSnap(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeftIcon className="h-4 w-4" />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="px-6 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600">
          <h1 className="text-2xl font-bold text-white flex items-center">
            <CoinsIcon className="h-6 w-6 mr-2" />
            CashSnap Administration
          </h1>
        </div>

        <div className="p-4">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircleIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  This page is for administrators only. You can mark winning options for ended CashSNAPs and distribute prizes to users who voted correctly.
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600"></div>
            </div>
          ) : pendingSnaps.length === 0 ? (
            <div className="text-center py-12">
              <TrophyIcon className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Pending CashSNAPs</h2>
              <p className="text-gray-600">
                There are no ended CashSNAPs waiting for winner selection at this time.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {pendingSnaps.map((snap) => (
                <div key={snap.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-100">
                    <h2 className="text-xl font-semibold text-yellow-800">{snap.title}</h2>
                    <div className="mt-1 text-sm text-gray-600">
                      <span>Ended {formatDistanceToNow(new Date(snap.ends_at), { addSuffix: true })}</span>
                      <span className="mx-2">•</span>
                      <span>{snap.total_votes} total votes</span>
                    </div>
                    {snap.description && (
                      <p className="mt-2 text-sm text-gray-700">{snap.description}</p>
                    )}
                  </div>
                  
                  <div className="px-6 py-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Select Winning Option:</h3>
                    <div className="space-y-4">
                      {snap.options.map((option) => (
                        <div 
                          key={option.id} 
                          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-medium text-gray-900">{option.title}</h4>
                              {option.description && (
                                <p className="mt-1 text-sm text-gray-600">{option.description}</p>
                              )}
                              <div className="mt-2 text-sm text-gray-500">
                                {option.vote_count} votes ({snap.total_votes > 0 ? Math.round((option.vote_count / snap.total_votes) * 100) : 0}%)
                              </div>
                            </div>
                            <Button
                              onClick={() => markWinnerAndDistributePrizes(snap.id, option.id)}
                              loading={processingSnap === snap.id}
                              disabled={processingSnap !== null}
                              className="bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500"
                              icon={<CheckIcon className="h-4 w-4" />}
                            >
                              Mark as Winner
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      <p>
                        <strong>Note:</strong> Marking a winner will automatically distribute prizes to users who voted for the correct option.
                        The prize distribution is weighted by user activity score and early voting bonus.
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}